import React, { Fragment } from "react";
import { path } from "ramda";
import { firstError, hasError } from "common/validation";
import { isBlank, notBlank } from "common/general";
import Grid from "@material-ui/core/Grid";
import Autocomplete from "@material-ui/lab/Autocomplete";
import MenuItem from "@material-ui/core/MenuItem";
import CustomizableTextField from "../../general-components/CustomizableTextField";
import HidableGridItem from "../../general-components/HidableGridItem";
import CustomizableSelect from "../../general-components/CustomizableSelect";
import { useTranslation } from "react-i18next";
import AutocompleteInputField from "../../general-components/AutocompleteInputField";
import { useLanguages } from "../use-languages";
import PublicationDate from "./PublicationDate";
import CustomizableRichTextField from "../../general-components/CustomizableRichTextField";

const VideoDetailsFields = ({
  errors,
  fields,
  onBlur,
  onFieldChange,
  onVideoRatingChange,
  videoRatings,
}) => {
  const { t } = useTranslation();
  const languages = useLanguages();

  return (
    <Fragment>
      <Grid container spacing={2}>
        <CustomizableTextField
          required
          id="video-title"
          fieldOption="title"
          xs={12}
          sm={6}
          defaultLabel={t("file_details.title", "Title")}
          value={fields.title || ""}
          onChange={onFieldChange(["title"])}
          error={hasError(errors, "title")}
          helperText={t(firstError(errors, "title"))}
          onBlur={onBlur("title", fields.title)}
          inputProps={{ maxLength: 2048, id: "video-title" }}
        />

        <CustomizableTextField
          id="video-subtitle"
          xs={12}
          sm={6}
          fieldOption="subtitle"
          defaultLabel={t("file_details.subtitle", "Subtitle (optional)")}
          value={fields.subtitle || ""}
          onChange={onFieldChange(["subtitle"])}
          error={hasError(errors, "subtitle")}
          helperText={t(firstError(errors, "subtitle"))}
          onBlur={onBlur("subtitle", fields.subtitle)}
          inputProps={{ maxLength: 255, id: "video-subtitle" }}
        />

        <CustomizableSelect
          xs={6}
          sm={3}
          fieldOption="language"
          defaultLabel={t("file_details.language", "Language")}
          value={fields.language || ""}
          onChange={onFieldChange(["language"])}
        >
          {languages.map(lang => (
            <MenuItem key={lang.code} value={lang.code}>
              {lang.translation}
            </MenuItem>
          ))}
        </CustomizableSelect>

        <HidableGridItem fieldOption="rating" xs={6} sm={3}>
          <Autocomplete
            id="video-rating"
            disableClearable
            autoSelect
            options={videoRatings}
            value={path(["rating"], fields) || ""}
            getOptionLabel={rating => (notBlank(rating) ? rating.display : "")}
            getOptionSelected={(opt, val) =>
              isBlank(val) || opt.name === val.name
            }
            onChange={onVideoRatingChange}
            renderInput={params => {
              params.inputProps["aria-autocomplete"] = "none";
              params.inputProps["id"] = "video-rating";
              return (
                <AutocompleteInputField
                  params={params}
                  fieldOption="rating"
                  onBlur={onBlur("rating", path(["rating"], fields))}
                  error={hasError(errors, "rating")}
                  helperText={t(firstError(errors, "rating"))}
                  name={"rating-" + Math.random()}
                  defaultLabel={t("file_details.rating", "Rating (optional)")}
                />
              );
            }}
          />
        </HidableGridItem>

        <PublicationDate />

        <CustomizableTextField
          id="video-series"
          fieldOption="series"
          xs={6}
          sm={6}
          defaultLabel={t("file_details.series", "Series (optional)")}
          value={fields.series || ""}
          onChange={onFieldChange(["series"])}
          error={hasError(errors, "series")}
          helperText={t(firstError(errors, "series"))}
          onBlur={onBlur("series", fields.series)}
          inputProps={{ maxLength: 255, id: "video-series" }}
        />

        <CustomizableTextField
          id="video-episode"
          fieldOption="episode"
          xs={6}
          sm={6}
          defaultLabel={t("file_details.episode", "Episode (optional)")}
          value={fields.episode || ""}
          onChange={onFieldChange(["episode"])}
          error={hasError(errors, "episode")}
          helperText={t(firstError(errors, "episode"))}
          onBlur={onBlur("episode", fields.episode)}
          inputProps={{ maxLength: 255, id: "video-episode" }}
        />

        <CustomizableRichTextField
          id="description"
          fieldOption="description"
          xs={12}
          sm={12}
          md={12}
          defaultLabel={t("file_details.description", "Description")}
          value={path(["descriptionText"], fields) || ""}
          onChange={({ text, html }) => {
            if (text === "") {
              onFieldChange(["description"])("");
              onFieldChange(["descriptionText"])("");
            } else {
              onFieldChange(["description"])(text);
              onFieldChange(["descriptionText"])(html);
            }
          }}
          error={hasError(errors, "description")}
          helperText={t(firstError(errors, "description"))}
          onBlur={onBlur("description", fields.description)}
        />

        <CustomizableTextField
          id="video-keywords"
          fieldOption="keywords"
          xs={12}
          sm={6}
          defaultLabel={t("file_details.keywords", "Keywords (optional)")}
          value={fields.keywords || ""}
          onChange={onFieldChange(["keywords"])}
          error={hasError(errors, "keywords")}
          helperText={
            t(firstError(errors, "keywords")) ||
            t(
              "file_details_keywords_helper_text",
              "Keywords should be separated by commas",
            )
          }
          onBlur={onBlur("keywords", fields.keywords)}
          multiline
          inputProps={{ maxLength: 1000, id: "video-keywords" }}
        />
      </Grid>
    </Fragment>
  );
};

export default VideoDetailsFields;
